/**
 * @file api.ts
 * @brief Company data controls (ADR-051): the admin's switches and the list
 *        of the company's content already shared outside it.
 *
 * @details
 *   useCompanyDataControls      → GET  /api/company/data-controls
 *   useSaveCompanyDataControls  → PUT  /api/company/data-controls (`{}` = notice seen)
 *   useExternalShares           → GET  /api/company/data-controls/external-shares
 *   useRemoveAllExternalShares  → POST /api/company/data-controls/external-shares/remove-all
 *
 * Always the admin's own company (no id in the URL); members get 403. A save
 * also refreshes the /me extras, so the admin's own share and export states
 * follow at once. Every change is logged by the server.
 *
 * @references
 * - scratch/claude-code-report.md "Company data controls: Part 1" section 1
 *
 * @author DES
 * @date 21-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPost, apiPut } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import { meExtrasKey } from "@/shared/auth/meExtras";
import { companyMeetingsKeys } from "@/features/company-meetings/api";

const DataControlsStateSchema = z
  .object({
    dataControls: z
      .object({
        shareInsideOnly: z.boolean(),
        adminsToo: z.boolean(),
        allowExport: z.boolean(),
        noticePending: z.boolean().optional().default(false),
        updatedAt: z.string().nullable().optional(),
        updatedBy: z.unknown().optional(),
      })
      .passthrough(),
    externalShareCount: z.number().optional().default(0),
    /** false: the server's off switch is on, nothing is restricted now. */
    enforced: z.boolean().optional().default(true),
  })
  .passthrough();
export type CompanyDataControlsState = z.infer<typeof DataControlsStateSchema>;
export type DataControlsPatch = Partial<
  Pick<CompanyDataControlsState["dataControls"], "shareInsideOnly" | "adminsToo" | "allowExport">
>;

const ExternalShareSchema = z
  .object({
    kind: z.string(),
    id: z.string(),
    title: z.string().nullable().optional(),
    person: z
      .object({
        userId: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    owner: z
      .object({ userId: z.string().nullable().optional(), name: z.string().nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
    sharedAt: z.string().nullable().optional(),
  })
  .passthrough();
export type ExternalShare = z.infer<typeof ExternalShareSchema>;

export const companyDataControlsKey = ["company", "data-controls"] as const;
const externalSharesKey = [...companyDataControlsKey, "external-shares"] as const;

export function useCompanyDataControls(enabled = true) {
  return useQuery<CompanyDataControlsState>({
    queryKey: companyDataControlsKey,
    enabled,
    queryFn: async () =>
      DataControlsStateSchema.parse(await apiGet<unknown>(companyEndpoints.dataControls)),
  });
}

export function useSaveCompanyDataControls() {
  const qc = useQueryClient();
  return useMutation<
    CompanyDataControlsState,
    Error,
    DataControlsPatch,
    { prev?: CompanyDataControlsState }
  >({
    mutationFn: async (body) =>
      DataControlsStateSchema.parse(await apiPut<unknown>(companyEndpoints.dataControls, body)),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: companyDataControlsKey });
      const prev = qc.getQueryData<CompanyDataControlsState>(companyDataControlsKey);
      if (prev) {
        qc.setQueryData<CompanyDataControlsState>(companyDataControlsKey, {
          ...prev,
          dataControls: { ...prev.dataControls, ...patch },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(companyDataControlsKey, ctx.prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(companyDataControlsKey, data);
      void qc.invalidateQueries({ queryKey: meExtrasKey });
      void qc.invalidateQueries({ queryKey: externalSharesKey });
    },
  });
}

export function useExternalShares(enabled: boolean) {
  return useQuery<ExternalShare[]>({
    queryKey: externalSharesKey,
    enabled,
    queryFn: async () => {
      const raw = await apiGet<{ shares?: unknown }>(companyEndpoints.dataControlsExternalShares);
      return z.array(ExternalShareSchema).catch([]).parse(raw?.shares ?? []);
    },
  });
}

export function useRemoveAllExternalShares() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      apiPost<{ removed?: { meetings?: number; notes?: number } }>(
        companyEndpoints.dataControlsRemoveAllExternal,
        {},
      ),
    onSuccess: () => {
      qc.setQueryData<ExternalShare[]>(externalSharesKey, []);
      void qc.invalidateQueries({ queryKey: companyDataControlsKey });
      // Card lines and access lists change too.
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: ["home", "meetings"] });
    },
  });
}
